import React, { useState, useEffect } from "react";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { SlideOver } from "@/components/SlideOver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { USER_ROLES, ROLE_LABELS } from "@/lib/constants";
import { api, apiError } from "@/lib/api";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function ClientForm({ client, onSaved }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const empty = { name: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" };
  const [v, setV] = useState(client ? {
    name: client.name,
    contact_name: client.contact_name || "",
    contact_email: client.contact_email || "", contact_phone: client.contact_phone || "",
    notes: client.notes || "",
  } : empty);
  const [result, setResult] = useState(null);

  function set(k, val) { setV((p) => ({ ...p, [k]: val })); }

  function closeAll() {
    setOpen(false);
    setResult(null);
    if (!client) setV(empty);
  }

  async function submit(e) {
    e.preventDefault(); setError(null); setSaving(true);
    try {
      if (client) {
        const { data } = await api.put(`/clients/${client.id}`, v);
        onSaved && onSaved();
        if (data && data.client_user_status) {
          setResult(data);
        } else {
          setOpen(false);
        }
      } else {
        const { data } = await api.post("/clients", v);
        onSaved && onSaved();
        if (data && data.client_user_status) {
          setResult(data);
        } else {
          setV(empty);
          setOpen(false);
        }
      }
    } catch (err) { setError(apiError(err)); } finally { setSaving(false); }
  }

  return (
    <>
      {client ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}><Pencil className="h-4 w-4" /> Edit</Button>
      ) : (
        <Button onClick={() => setOpen(true)} data-testid="add-organization-btn"><Plus className="h-4 w-4" /> Add Organization</Button>
      )}
      <SlideOver open={open} onClose={closeAll} title={result ? "Organization Added" : (client ? `Edit ${client.name}` : "Add Organization")}>
        {result ? (
          <div className="space-y-4" data-testid="client-created-result">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <p className="font-semibold">{client ? "Organization updated" : "Organization added successfully"}</p>
              {result.client_user_status === 'created' && (
                <p className="mt-1">A user login has been created for <span className="font-mono font-semibold">{result.client_user_email}</span> and a welcome email with the temporary password has been sent. They&apos;ll be asked to choose a new password on first sign in.</p>
              )}
              {result.client_user_status === 'existing_user' && (
                <p className="mt-1">An account already exists for <span className="font-mono font-semibold">{result.client_user_email}</span>, so no new login was created. Reassign that user to this organization from the Users page if needed.</p>
              )}
            </div>
            <Button type="button" className="w-full" onClick={closeAll}>Done</Button>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <div className="space-y-2">
            <Label htmlFor="name">Organization Name *</Label>
            <Input id="name" value={v.name} onChange={(e) => set("name", e.target.value)} required placeholder="e.g. T-Mobile" data-testid="organization-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact_name">Contact Name</Label>
            <Input id="contact_name" value={v.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact_email">Contact Email</Label>
            <Input id="contact_email" type="email" value={v.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact_phone">Contact Phone</Label>
            <Input id="contact_phone" value={v.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea id="notes" rows={3} value={v.notes} onChange={(e) => set("notes", e.target.value)} className={textareaClass} />
          </div>
          {!client && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
              If a contact email is provided, an organization-user login will be auto-created with a temporary password and emailed to that contact. They&apos;ll be able to view their inventory and create/view tickets.
            </div>
          )}
          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {client ? "Save Changes" : "Add Organization"}
          </Button>
        </form>
        )}
      </SlideOver>
    </>
  );
}

export function UserForm({ clients, user, onSaved, presetClientId, buttonLabel, buttonSize, buttonVariant }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const empty = {
    email: "", full_name: "",
    role: presetClientId ? "client_user" : "warehouse_staff",
    client_id: presetClientId || "",
    password: "", is_active: true,
    client_role: "user",
  };
  const editVals = user ? {
    email: user.email, full_name: user.full_name || "", role: user.role,
    client_id: user.client_id || "", new_password: "", is_active: !!user.is_active,
    client_role: user.client_role || "user",
  } : empty;
  const [v, setV] = useState(editVals);

  function set(k, val) { setV((p) => ({ ...p, [k]: val })); }

  async function submit(e) {
    e.preventDefault(); setError(null); setSaving(true);
    try {
      if (user) {
        const payload = {
          full_name: v.full_name, role: v.role, client_id: v.client_id || null,
          is_active: v.is_active, new_password: v.new_password || null,
        };
        if (v.role === "client_user") payload.client_role = v.client_role || "user";
        await api.put(`/users/${user.id}`, payload);
        setOpen(false);
        onSaved && onSaved();
      } else {
        const payload = {
          email: v.email, full_name: v.full_name,
          role: v.role, client_id: v.client_id || null, is_active: v.is_active,
        };
        if (v.role === "client_user") payload.client_role = v.client_role || "user";
        await api.post("/users", payload);
        setV(empty);
        setOpen(false);
        onSaved && onSaved();
      }
    } catch (err) { setError(apiError(err)); } finally { setSaving(false); }
  }

  return (
    <>
      {user ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}><Pencil className="h-4 w-4" /> Edit</Button>
      ) : (
        <Button size={buttonSize} variant={buttonVariant} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> {buttonLabel || "Add User"}</Button>
      )}
      <SlideOver open={open} onClose={() => setOpen(false)} title={user ? `Edit ${user.email}` : "Add User"}>
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          {!user && (
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={v.email} onChange={(e) => set("email", e.target.value)} required />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name</Label>
            <Input id="full_name" value={v.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </div>
          {presetClientId ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              This user will be added as an <span className="font-semibold">Organization User</span> for <span className="font-semibold">{clients[0]?.name || 'this organization'}</span>.
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <select id="role" className={selectClass} value={v.role} onChange={(e) => set("role", e.target.value)}>
                  {USER_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </div>
              {v.role === "client_user" && (
                <div className="space-y-2">
                  <Label htmlFor="client_id">Organization *</Label>
                  <select id="client_id" className={selectClass} value={v.client_id} onChange={(e) => set("client_id", e.target.value)} required>
                    <option value="">Select an organization...</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </>
          )}
          {v.role === "client_user" && v.client_id && (
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={v.client_role === "super_admin"}
                onChange={(e) => set("client_role", e.target.checked ? "super_admin" : "user")}
                className="mt-0.5 h-4 w-4 rounded border-input"
                data-testid="user-form-super-admin"
              />
              <span>
                <span className="font-medium">Super Admin</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Can add, edit and remove users inside their organisation.
                </span>
              </span>
            </label>
          )}
          {!user && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
              A temporary password will be generated and emailed to this user. They will be asked to choose a new password on first sign in.
            </div>
          )}
          {user && (
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password (leave blank to keep current)</Label>
              <Input id="new_password" type="password" value={v.new_password} onChange={(e) => set("new_password", e.target.value)} />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={v.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 rounded border-input" />
            Active
          </label>
          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {user ? "Save Changes" : "Add User"}
          </Button>
        </form>
      </SlideOver>
    </>
  );
}
